/**
 * PrepMasters Read Aloud / Audio Study Mode — cloud-first TTS engine.
 *
 * Architecture (2026-02):
 *   Primary path: server-generated MP3 via /api/tts/audio (OpenAI tts-1-hd,
 *   cached on the Vultr filesystem, played back through HTML5 <audio>).
 *   This is now the SAME on every runtime — iOS native, Android native,
 *   Safari, Chrome, Firefox — because MP3 playback is universal and no
 *   longer relies on WKWebView's flaky Web Speech / native TTS plugin.
 *
 *   Fallback: Web Speech API (`window.speechSynthesis`). Only used if
 *   we're on a device that has NO network AND already downloaded cached
 *   audio isn't available.
 *
 * Public API preserved (same as pre-cloud code) so nothing else has to
 * change: speak(), cancelSpeech(), pauseSpeech(), resumeSpeech(),
 * previewVoice(), loadVoices(), isTTSSupported(), chunkSentences(),
 * rateFor(), delay().
 */

import { pickBestVoice } from "./voiceFilter";

const SPEED_MAP = { slow: 0.9, normal: 1.0, fast: 1.15 };

// OpenAI tts-1-hd hard limit is 4096 chars; we chunk conservatively so
// that a single sentence never straddles a request boundary.
const CLOUD_MAX_CHARS = 3800;

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Single <audio> element reused across the session — creating a new one
// per chunk on iOS makes the WKWebView audio session flap and produces
// audible clicks between chunks.
let _audio = null;
function _getAudio() {
  if (_audio) return _audio;
  const a = new Audio();
  a.preload = "auto";
  // Playback rate persists across .src assignments in most browsers, but we
  // reset it at each speak() call anyway.
  _audio = a;
  return _audio;
}

// Active session state so cancelSpeech() can abort a chunk loop mid-play
// and any pending fetch.
let _activeCancel = null;

// Cache of blob-URL per (text|voice|speed) to avoid re-downloading during
// a review session where the same question is spoken multiple times
// (e.g. student pauses/resumes). Cleaned up on cancelSpeech().
const _sessionBlobs = new Map();

function _cacheKey(text, voice, speed) {
  return `${voice}|${speed.toFixed(2)}|${text}`;
}

async function _fetchAudioBlob(text, voice, speed, abortSig) {
  const key = _cacheKey(text, voice, speed);
  const cached = _sessionBlobs.get(key);
  if (cached) return cached;
  const params = new URLSearchParams({ text, voice, speed: speed.toString() });
  const token = localStorage.getItem("pm_token");
  const res = await fetch(`${API_BASE}/api/tts/audio?${params.toString()}`, {
    signal: abortSig,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(`Cloud TTS ${res.status}: ${msg.slice(0, 120) || res.statusText}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  _sessionBlobs.set(key, url);
  return url;
}

function _clearBlobCache() {
  for (const url of _sessionBlobs.values()) {
    try { URL.revokeObjectURL(url); } catch { /* noop */ }
  }
  _sessionBlobs.clear();
}

export function isTTSSupported() {
  // We can always talk to /api/tts/audio; HTML5 <audio> exists everywhere.
  // The only failure mode is offline + never-downloaded content.
  return typeof window !== "undefined" && typeof Audio !== "undefined";
}

/**
 * Returns a Promise that resolves to a list of voice objects
 * (`{ voiceURI, name, lang, localService, default }`). Kept as an async
 * function so calling code that awaits it still works — even though
 * the cloud path uses a fixed voice roster.
 */
export function loadVoices() {
  return Promise.resolve([
    { voiceURI: "nova",    name: "Nova (Warm, Female)",        lang: "en-US", localService: false, default: true },
    { voiceURI: "shimmer", name: "Shimmer (Bright, Female)",   lang: "en-US", localService: false, default: false },
    { voiceURI: "alloy",   name: "Alloy (Neutral)",            lang: "en-US", localService: false, default: false },
    { voiceURI: "echo",    name: "Echo (Calm, Male)",          lang: "en-US", localService: false, default: false },
    { voiceURI: "onyx",    name: "Onyx (Deep, Male)",          lang: "en-US", localService: false, default: false },
    { voiceURI: "fable",   name: "Fable (Storyteller)",        lang: "en-US", localService: false, default: false },
  ]);
}

/** Splits text into sentence chunks of at most `max` chars each, with
 *  sentence-boundary detection so we never cut mid-word. */
export function chunkSentences(text, max = CLOUD_MAX_CHARS) {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean ? [clean] : [];
  const parts = clean.split(/(?<=[.!?])\s+/);
  const chunks = [];
  let cur = "";
  for (const p of parts) {
    if ((cur + " " + p).trim().length > max && cur) {
      chunks.push(cur.trim());
      cur = p;
    } else {
      cur = (cur ? cur + " " : "") + p;
    }
  }
  if (cur.trim()) chunks.push(cur.trim());
  return chunks;
}

export function rateFor(speedTag) {
  if (typeof speedTag === "number") return speedTag;
  return SPEED_MAP[speedTag] || 1.0;
}

/**
 * Cancellable delay. Returns `{ promise, cancel }` — matches the shape the
 * review-mode chain (StudentReviewMode.jsx) has always consumed. On
 * cancel we resolve immediately so callers awaiting `d.promise` don't
 * hang; they usually gate further work on a runId sentinel anyway.
 */
export function delay(ms) {
  let cancelled = false;
  let timer = null;
  let resolveFn = null;
  const promise = new Promise((resolve) => {
    resolveFn = resolve;
    timer = setTimeout(() => { if (!cancelled) resolve(); }, ms);
  });
  return {
    promise,
    cancel: () => {
      if (cancelled) return;
      cancelled = true;
      if (timer) { clearTimeout(timer); timer = null; }
      // Resolve immediately so `await d.promise` in the review chain
      // returns cleanly — chain teardown continues via its runId check.
      resolveFn?.();
    },
  };
}

/** Speaks a chunk via HTML5 audio. Returns a Promise that resolves when
 *  playback ends, rejects on error or cancellation. */
function _speakChunk(text, { voice, speed, abortSig }) {
  return new Promise(async (resolve, reject) => {
    let url;
    try {
      url = await _fetchAudioBlob(text, voice, speed, abortSig);
    } catch (e) {
      return reject(e);
    }
    if (abortSig?.aborted) return reject(new Error("cancelled"));
    const audio = _getAudio();
    audio.src = url;
    audio.playbackRate = 1.0;  // OpenAI already renders at requested speed
    const onEnded = () => { cleanup(); resolve(); };
    const onError = () => { cleanup(); reject(new Error("Audio playback failed")); };
    const onAbort = () => { cleanup(); reject(new Error("cancelled")); };
    const cleanup = () => {
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      abortSig?.removeEventListener?.("abort", onAbort);
    };
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    abortSig?.addEventListener?.("abort", onAbort);
    audio.play().catch((e) => {
      cleanup();
      reject(new Error(e?.message || "Audio playback blocked"));
    });
  });
}

/**
 * Public entry point. Speaks text using the cloud MP3 pipeline, chunking
 * long text into <4KB requests. Options:
 *   - voice: "nova" | "alloy" | "echo" | "onyx" | ... (default nova)
 *   - rate: number (default 1.0). 0.25 <= rate <= 4.0.
 *   - lang: ignored (OpenAI auto-detects language).
 *   - chunk: bool (default true). When false, first chunk only.
 *
 * IMPORTANT: If a `speak()` call is already in flight, we abort it before
 * starting the new one. Previously we just overwrote `_activeCancel`
 * without invoking the old controller, which left the old chunk-loop
 * running against the SHARED <audio> element — two concurrent chains
 * would reassign `audio.src` back-to-back and the student heard
 * "a few words then the whole clip re-read from the top."
 *
 * We also no longer fall back to Web Speech on ANY cloud failure — the
 * old fallback re-read the same text with the phone's built-in voice
 * whenever a play() was interrupted by a race or a src swap, which
 * looked identical to the double-read bug. If the cloud path truly
 * fails, we surface the error to the caller so the UI can toast.
 */
export function speak(text, options = {}) {
  return new Promise(async (resolve, reject) => {
    const clean = (text || "").trim();
    if (!clean) return resolve();
    const voice = options.voice?.voiceURI || options.voice?.name || options.voice || "nova";
    const speed = Math.max(0.25, Math.min(4.0, options.rate ?? 1.0));

    // Abort any previous in-flight speak() so it can't race the new one
    // on the shared <audio> element.
    if (typeof _activeCancel === "function") {
      try { _activeCancel(); } catch { /* noop */ }
      _activeCancel = null;
    }

    const controller = new AbortController();
    const cancelFn = () => controller.abort();
    cancelFn.__ctrl = controller;
    _activeCancel = cancelFn;
    const chunks = options.chunk === false ? [clean.slice(0, CLOUD_MAX_CHARS)] : chunkSentences(clean);
    try {
      for (const c of chunks) {
        if (controller.signal.aborted) throw new Error("cancelled");
        await _speakChunk(c, { voice, speed, abortSig: controller.signal });
      }
      resolve();
    } catch (e) {
      // Treat AbortError / "cancelled" / "aborted" / signal-abort as a
      // clean shutdown — the caller (review-mode chain) will decide
      // whether to move on via its runId gate.
      const msg = String(e?.message || e || "");
      const name = String(e?.name || "");
      if (name === "AbortError" || /cancel|abort/i.test(msg) || controller.signal.aborted) {
        return resolve();
      }
      reject(e);
    } finally {
      // Only clear the cancel handle if it still points at OUR
      // controller — a newer speak() may have already installed its own.
      if (_activeCancel && _activeCancel.__ctrl === controller) _activeCancel = null;
    }
  });
}

export function pauseSpeech() {
  try { _getAudio().pause(); } catch { /* noop */ }
  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    try { window.speechSynthesis.pause(); } catch { /* noop */ }
  }
}

export function resumeSpeech() {
  try { _getAudio().play?.().catch(() => { /* noop */ }); } catch { /* noop */ }
  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    try { window.speechSynthesis.resume(); } catch { /* noop */ }
  }
}

export function cancelSpeech() {
  if (typeof _activeCancel === "function") {
    try { _activeCancel(); } catch { /* noop */ }
    _activeCancel = null;
  }
  try {
    const a = _getAudio();
    a.pause();
    a.removeAttribute("src");
    a.load();
  } catch { /* noop */ }
  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
  }
  _clearBlobCache();
}

/** Preview a voice on the settings screen — plays a short sample. */
export function previewVoice(voice) {
  const v = voice?.voiceURI || voice?.name || voice || "nova";
  return speak(
    "Hi. I'll be reading your PrepMasters review content aloud.",
    { voice: v },
  );
}

// The old auto-voice helper is now a no-op — the cloud voice is chosen by
// the caller. Kept exported so existing imports don't break.
export function pickAutoVoice(voices) {
  return pickBestVoice(voices);
}
